import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import cn from "classnames";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { SOCIAL_SIGN_IN } from "@api/mutationsV2/profile";
import { GET_PROFILE, SOCIAL_AUTH_PROVIDERS } from "@api/queriesV2/profile";
import { currencies } from "@config/config";
import { useStore } from "@hooks/useZustandProvider";
import {
  prepareFacebookLogin,
  renderGoogleSignInButton,
  requestFacebookAccessToken,
} from "@lib/socialProviderSdk";
import Button from "@ui-kit/Button/Button";

import s from "./SocialSignIn.module.scss";

const GOOGLE_PROVIDER = "GOOGLE";
const FACEBOOK_PROVIDER = "FACEBOOK";
/* In some in-app browsers `FB.login` opens its window and never calls back. Nothing would then
   release the lock, and the button would stay disabled until the traveller reloads the page. */
const FACEBOOK_LOGIN_LOCK_MS = 30000;

const FacebookIcon = () => (
  <svg className={s.facebookIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#1877F2"
      d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.414c0-3.025 1.79-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.973h-1.513c-1.49 0-1.956.931-1.956 1.887v2.26h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z"
    />
  </svg>
);

const SocialSignIn = ({ children, className, onSuccess }) => {
  const { t } = useTranslation(["common"]);
  const { resetProfile, updateProfile, updateModals, changeCurrency } =
    useStore();
  const { asPath, locale, defaultLocale } = useRouter();
  const googleButtonRef = useRef(null);
  const busyRef = useRef(false);
  const methodRef = useRef(null);
  const authenticateRef = useRef(() => undefined);
  const [busy, setBusy] = useState(false);
  const [googleButton, setGoogleButton] = useState("loading");
  const [facebookSdk, setFacebookSdk] = useState("loading");

  const { data, loading: providersLoading } = useQuery(SOCIAL_AUTH_PROVIDERS, {
    fetchPolicy: "cache-first",
    context: { clientName: "v2" },
  });

  const providers = data?.socialAuthProviders;
  const google = providers?.find(
    ({ provider }) => provider === GOOGLE_PROVIDER,
  );
  const facebook = providers?.find(
    ({ provider }) => provider === FACEBOOK_PROVIDER,
  );
  const googleClientId = google?.clientId || "";
  const facebookAppId = facebook?.clientId || "";
  const facebookSdkVersion = facebook?.sdkVersion || "";
  const hasGoogle = Boolean(googleClientId);
  const hasFacebook = Boolean(facebookAppId && facebookSdkVersion);

  const setLock = (locked) => {
    busyRef.current = locked;
    setBusy(locked);
    updateProfile({ loading: locked });
  };

  // Keep this hydration in step with the password path in `PersonalForm.jsx`.
  const [getProfile] = useLazyQuery(GET_PROFILE, {
    fetchPolicy: "network-only",
    context: { clientName: "v2" },
    onCompleted: (data) => {
      if (data.profile) {
        const profile = data.profile;

        const isHome = asPath === "/" && locale === defaultLocale;

        resetProfile();
        updateProfile({
          ...profile,
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
          PhoneAreaCode: profile.PhoneAreaCode?.replace("+", "") || "",
          loading: false,
        });
        updateModals({ isOpenLoginModal: false });

        try {
          window.exponea?.identify?.({ email: profile.email });
        } catch (error) {
          console.error("Failed to identify the user in analytics", error);
        }

        try {
          // The provider token carries no e-mail, so the event waits for the profile.
          window.dataLayer?.push({
            event: "login",
            email: profile.email,
            method: methodRef.current,
          });
        } catch (error) {
          console.error("Failed to track login", error);
        }

        if (isHome) {
          const currency = getCookie("currency");

          if (!currency) {
            const currency = currencies.find(
              (c) => c.Currency === profile.currency,
            );

            if (currency) {
              setCookie("currency", currency.CurrencyName);
              changeCurrency(currency.CurrencyName);
            }
          }
        }

        /* The caller decides where a finished sign-in leads: the modal closes itself, while
           registration sends the traveller to the homepage, as the password flow does. */
        onSuccess?.();
      } else {
        updateProfile({ loading: false });
        updateModals({ isOpenLoginModal: false });
      }

      setLock(false);
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
      updateProfile({ loading: false });
      updateModals({ isOpenLoginModal: false });
      setLock(false);
    },
  });

  const [socialSignIn] = useMutation(SOCIAL_SIGN_IN, {
    context: { clientName: "v2" },
    onCompleted: (data) => {
      const result = data?.socialSignin;
      const token = result?.token;

      if (!token) {
        setLock(false);

        // The backend reason is written for the traveller; fall back when it sends none.
        return toast.error(
          result?.ErrorReason
            ? `[${result.ErrorCode}]: ${result.ErrorReason}`
            : t("modal.login.social.failed"),
        );
      }

      setCookie("token", token);
      deleteCookie("corporate-token");
      deleteCookie("isCorporate");
      getProfile();
    },
    onError: (error) => {
      setLock(false);
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const finishAuthentication = async (provider, providerToken) => {
    methodRef.current = provider.toLowerCase();
    setLock(true);

    try {
      await socialSignIn({ variables: { input: { provider, providerToken } } });
    } catch {
      // Apollo reports this through `onError`; the rejection must not escape the SDK callback.
      console.error("Social sign-in failed");
    }
  };

  /* Google's callback can arrive at any time, including while a sign-in is already running. The
     Facebook path takes the lock before it gets this far and hands over directly. */
  const authenticate = async (provider, providerToken) => {
    if (busyRef.current) return;

    await finishAuthentication(provider, providerToken);
  };

  authenticateRef.current = authenticate;

  useEffect(() => {
    if (!hasGoogle || !googleButtonRef.current) return;

    const element = googleButtonRef.current;
    let cleanup;
    let mounted = true;

    setGoogleButton("loading");
    void renderGoogleSignInButton(
      element,
      googleClientId,
      locale,
      (credential) => {
        void authenticateRef.current(GOOGLE_PROVIDER, credential);
      },
      (ready) => {
        /* React 18 drops an `inert` prop, so the attribute is set here. Until the SDK has sized
           its frame the host must take neither a click nor focus. */
        element.toggleAttribute("inert", !ready);

        if (ready) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", "true");
        }

        if (mounted) setGoogleButton(ready ? "ready" : "loading");
      },
    )
      .then((dispose) => {
        if (mounted) {
          cleanup = dispose;
          setGoogleButton("ready");
        } else {
          dispose();
        }
      })
      .catch(() => {
        console.error("Google Identity Services failed to initialize");

        if (mounted) setGoogleButton("failed");
      });

    return () => {
      mounted = false;
      cleanup?.();
    };
  }, [googleClientId, hasGoogle, locale]);

  useEffect(() => {
    if (!hasFacebook) return;

    let mounted = true;

    setFacebookSdk("loading");
    void prepareFacebookLogin(facebookAppId, facebookSdkVersion)
      .then(() => {
        if (mounted) setFacebookSdk("ready");
      })
      .catch(() => {
        console.error("Facebook Login failed to initialize");

        if (mounted) setFacebookSdk("failed");
      });

    return () => {
      mounted = false;
    };
  }, [facebookAppId, facebookSdkVersion, hasFacebook]);

  useEffect(() => () => updateProfile({ loading: false }), [updateProfile]);

  const handleFacebook = async () => {
    if (facebookSdk === "loading" || busyRef.current) return;

    setLock(true);

    let ownsLock = true;
    let handedOff = false;
    const releaseLock = setTimeout(() => {
      ownsLock = false;
      setLock(false);
    }, FACEBOOK_LOGIN_LOCK_MS);

    try {
      if (facebookSdk === "failed") {
        // The SDK could not be loaded when the buttons appeared; try once more on demand.
        await prepareFacebookLogin(facebookAppId, facebookSdkVersion);
        setFacebookSdk("ready");
      }

      const accessToken = await requestFacebookAccessToken();

      if (!ownsLock) {
        // The lock timed out; a late answer is honoured unless another sign-in has started since.
        if (busyRef.current) return;

        setLock(true);
        ownsLock = true;
      }

      if (accessToken) {
        handedOff = true;
        await finishAuthentication(FACEBOOK_PROVIDER, accessToken);
      }
    } catch (error) {
      // Only our own errors reach here, so the message carries no token material.
      console.error("Facebook sign-in failed", error);
      toast.error(t("modal.login.social.failed"));
    } finally {
      clearTimeout(releaseLock);

      // Once handed over, the mutation and profile handlers own the lock.
      if (ownsLock && !handedOff) setLock(false);
    }
  };

  if (providersLoading) return null;

  if (!hasGoogle && !hasFacebook) return null;

  return (
    <div className={cn(s.social, className)} aria-busy={busy}>
      <div className={s.divider} aria-hidden="true">
        <span className={s.dividerLine} />
        <span className={s.dividerLabel}>{t("modal.login.social.or")}</span>
        <span className={s.dividerLine} />
      </div>
      {hasGoogle && googleButton !== "failed" && (
        <div className={s.googleSlot}>
          {/* The SDK keeps its iframe at 0x0 until the button inside has painted, so the host
              needs no hiding of its own: `visibility: hidden` here made Chrome throttle the frame
              and delayed that paint by seconds. The skeleton covers the slot instead. */}
          <div
            ref={googleButtonRef}
            data-google-button-host
            className={s.googleHost}
          />
          {googleButton !== "ready" && (
            <div
              data-google-button-placeholder
              aria-hidden="true"
              className={s.googleSkeleton}
            >
              <span className={s.googleSkeletonLogo} />
              <span className={s.googleSkeletonLabel} />
            </div>
          )}
        </div>
      )}
      {hasFacebook && (
        <Button
          flat
          className={s.facebook}
          disabled={busy || facebookSdk === "loading"}
          onClick={() => void handleFacebook()}
        >
          <FacebookIcon />
          {t("modal.login.social.continueWithFacebook")}
        </Button>
      )}
      {children}
    </div>
  );
};

export default SocialSignIn;
